import type { VstsBoardColumn } from "./VstsSourceSettings.types";
import type { VstsColumnOption } from "./VstsSourceSettings.ViewModel.types";

/**
 * Suffix appended to the key of the "done" half of a split column.
 *
 * Splitting is set per column, so a board holds any mix of split and plain
 * columns. Azure DevOps returns a split one as a single entry with
 * `isSplit: true`; the server expands it into two entries that share the same
 * `name` and differ only by `isDone`, so `name` on its own cannot identify a
 * column on a board where any column is split.
 */
export const VSTS_SPLIT_COLUMN_DONE_KEY_SUFFIX = "::done";

/**
 * Builds a stable, unique key for a board column.
 *
 * Mirrors the server-side `Column.IsEqual(name, done)` identity: a column is
 * identified by its name plus, for the halves of a split column, which side of
 * the split it is. Non-split columns keep their plain name as their key.
 */
export const getVstsColumnKey = (column: VstsBoardColumn): string => {
  const name = column.name ?? column.id ?? "";
  return column.isDone === true
    ? `${name}${VSTS_SPLIT_COLUMN_DONE_KEY_SUFFIX}`
    : name;
};

/** Finds the column matching `key`, or `undefined` when none matches. */
export const findVstsColumnByKey = (
  columns: VstsBoardColumn[],
  key: string,
): VstsBoardColumn | undefined =>
  columns.find((column) => getVstsColumnKey(column) === key);

/**
 * Keeps only the `selected` columns that still exist in `available`, returned
 * in `available` order so the server-side "last column" semantics follow the
 * board layout.
 */
export const reconcileVstsColumns = (
  selected: VstsBoardColumn[],
  available: VstsBoardColumn[],
): VstsBoardColumn[] => {
  const selectedKeys = new Set(selected.map(getVstsColumnKey));
  return available.filter((column) =>
    selectedKeys.has(getVstsColumnKey(column)),
  );
};

/**
 * Picker options for the column selectors, one per column entry: a plain
 * column yields one option, a split column yields an independently selectable
 * option per half.
 */
export const getVstsColumnOptions = (
  columns: VstsBoardColumn[],
): VstsColumnOption[] =>
  columns.map((column) => ({
    id: getVstsColumnKey(column),
    label: column.displayName ?? column.name ?? column.id ?? "",
  }));

/**
 * Options for the board-column → board-status mapping table.
 *
 * Board-mode status mappings are matched server-side by column *name* alone
 * (a work item's `sourceStatusId` is the raw column name), so both halves of a
 * split column collapse into a single mapping row. Plain columns are
 * unaffected and keep one row each.
 */
export const getVstsStatusMappingColumnOptions = (
  columns: VstsBoardColumn[],
): VstsColumnOption[] => {
  const seen = new Set<string>();
  const options: VstsColumnOption[] = [];

  for (const column of columns) {
    const name = column.name ?? column.id ?? "";
    if (!name || seen.has(name)) {
      continue;
    }
    seen.add(name);
    options.push({ id: name, label: name });
  }

  return options;
};
